import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { attachSessionGuard } from '../sessionGuard';
import {
  ShoppingBag, Gavel, Tag, ShieldCheck, BarChart2,
  CheckCircle, XCircle, Star, StarOff, RefreshCw,
  ChevronDown, Search, Plus, Edit2, Trash2, Eye,
  AlertTriangle, Clock, TrendingUp, Users, DollarSign,
  Settings, Save, ToggleLeft, ToggleRight,
} from 'lucide-react';

const API = 'https://api.jamboricg.com/api/guliohub';
const NAV  = '#001f3f';
const TEAL = '#0d9488';

const authHeader = () => {
  // `token` is the canonical key written by the active admin login flow.
  // Keep `authToken` only as a backwards-compatible fallback for existing sessions.
  const token = localStorage.getItem('token') || localStorage.getItem('authToken');
  return token ? { Authorization: `Token ${token}` } : {};
};

const api = axios.create({ baseURL: API });
// Four-hour session expiry, same rule as everywhere else.
attachSessionGuard(api);

api.interceptors.request.use(cfg => {
  cfg.headers = { ...cfg.headers, ...authHeader() };
  return cfg;
});

// ── atoms ──────────────────────────────────────────────────────────────────────

const Badge = ({ label, color }) => {
  const colors = {
    green:  'bg-emerald-100 text-emerald-700',
    yellow: 'bg-amber-100 text-amber-700',
    red:    'bg-red-100 text-red-700',
    gray:   'bg-slate-100 text-slate-600',
    blue:   'bg-blue-100 text-blue-700',
    teal:   'bg-teal-100 text-teal-700',
  };
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${colors[color] || colors.gray}`}>
      {label}
    </span>
  );
};

const StatusBadge = ({ status }) => {
  const map = {
    verified: ['Verified',   'green'],
    active:   ['Active',     'green'],
    draft:    ['Draft',      'blue'],
    paused:   ['Paused',     'gray'],
    approved: ['Approved',   'green'],
    pending:  ['Pending',    'yellow'],
    pending_image_review: ['Image Review', 'yellow'],
    rejected: ['Rejected',   'red'],
    flagged:  ['Flagged',    'red'],
    suspended:['Suspended',  'gray'],
    ended:    ['Ended',      'gray'],
    sold:     ['Sold',       'blue'],
    deleted:  ['Deleted',    'gray'],
  };
  const [label, color] = map[status] || [status, 'gray'];
  return <Badge label={label} color={color} />;
};

const StatCard = ({ icon: Icon, label, value, sub, color = TEAL }) => (
  <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100 flex items-start gap-3">
    <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
         style={{ backgroundColor: `${color}18` }}>
      <Icon size={20} style={{ color }} />
    </div>
    <div>
      <p className="text-xs text-slate-400 font-medium">{label}</p>
      <p className="text-xl font-bold text-slate-800">{value ?? '—'}</p>
      {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
    </div>
  </div>
);

const Btn = ({ onClick, disabled, children, variant = 'primary', size = 'sm', className = '' }) => {
  const base = 'inline-flex items-center gap-1.5 font-semibold rounded-lg transition-all';
  const sizes = { sm: 'px-3 py-1.5 text-xs', md: 'px-4 py-2 text-sm' };
  const variants = {
    primary:  'text-white shadow-sm hover:opacity-90',
    danger:   'bg-red-500 text-white hover:bg-red-600',
    ghost:    'bg-slate-100 text-slate-600 hover:bg-slate-200',
    success:  'bg-emerald-500 text-white hover:bg-emerald-600',
    outline:  'border border-slate-200 text-slate-600 hover:bg-slate-50',
  };
  const style = variant === 'primary' ? { backgroundColor: NAV } : {};
  return (
    <button onClick={onClick} disabled={disabled}
      style={style}
      className={`${base} ${sizes[size]} ${variants[variant]} ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`}>
      {children}
    </button>
  );
};

const SearchBar = ({ value, onChange, placeholder }) => (
  <div className="relative">
    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
    <input
      type="text"
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder || 'Search…'}
      className="pl-8 pr-3 py-1.5 text-sm border border-slate-200 rounded-lg w-56 focus:outline-none focus:ring-2 focus:ring-teal-300"
    />
  </div>
);

const FilterSelect = ({ value, onChange, options }) => (
  <div className="relative">
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      className="appearance-none pl-3 pr-8 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-300 bg-white"
    >
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
    <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
  </div>
);

const Modal = ({ open, onClose, title, children, width = 'max-w-lg' }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className={`bg-white rounded-2xl shadow-xl w-full ${width} max-h-[90vh] overflow-y-auto`}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h2 className="text-base font-bold text-slate-800">{title}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl leading-none">&times;</button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
};

const Toast = ({ msg, type }) => {
  if (!msg) return null;
  const bg = type === 'error' ? 'bg-red-500' : 'bg-emerald-500';
  return (
    <div className={`fixed top-4 right-4 z-[100] ${bg} text-white px-4 py-2.5 rounded-lg shadow-lg text-sm font-medium`}>
      {msg}
    </div>
  );
};

const fmtTsh  = n => `Tsh ${Number(n || 0).toLocaleString()}`;
const fmtDate = s => s ? new Date(s).toLocaleDateString('en-TZ', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

// ── Analytics tab ──────────────────────────────────────────────────────────────

const AnalyticsTab = () => {
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/admin/analytics/')
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-teal-400 border-t-transparent rounded-full animate-spin" /></div>;
  if (!data)   return <p className="text-center text-slate-400 py-20">Could not load analytics.</p>;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={Users}       label="Total Sellers"    value={data.sellers.total}     sub={`${data.sellers.verified} verified`} />
        <StatCard icon={ShoppingBag} label="Total Products"   value={data.products.total}    sub={`${data.products.active} live`} color="#2563EB" />
        <StatCard icon={Gavel}       label="Mnadani"          value={data.auctions.total}    sub={`${data.auctions.active} active`} color="#7C3AED" />
        <StatCard icon={DollarSign}  label="Unlock Revenue"   value={fmtTsh(data.contact_unlocks.revenue)} sub={`${data.contact_unlocks.total} unlocks`} color="#D97706" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Sellers breakdown */}
        <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100">
          <h3 className="text-sm font-bold text-slate-700 mb-4 flex items-center gap-2"><Users size={15} /> Sellers</h3>
          {[
            { label: 'Verified',  value: data.sellers.verified,  color: 'bg-emerald-500' },
            { label: 'Pending',   value: data.sellers.pending,   color: 'bg-amber-400' },
            { label: 'Rejected',  value: data.sellers.rejected,  color: 'bg-red-400' },
            { label: 'Suspended', value: data.sellers.suspended, color: 'bg-slate-400' },
          ].map(row => (
            <div key={row.label} className="flex items-center gap-3 mb-2">
              <div className={`w-2 h-2 rounded-full ${row.color}`} />
              <span className="text-xs text-slate-600 flex-1">{row.label}</span>
              <span className="text-sm font-bold text-slate-800">{row.value}</span>
            </div>
          ))}
        </div>

        {/* Products breakdown */}
        <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100">
          <h3 className="text-sm font-bold text-slate-700 mb-4 flex items-center gap-2"><ShoppingBag size={15} /> Products</h3>
          {[
            { label: 'Active',    value: data.products.active,   color: 'bg-emerald-500' },
            { label: 'Pending',   value: data.products.pending,  color: 'bg-amber-400' },
            { label: 'Flagged',   value: data.products.flagged,  color: 'bg-red-400' },
            { label: 'Featured',  value: data.products.featured, color: 'bg-blue-400' },
          ].map(row => (
            <div key={row.label} className="flex items-center gap-3 mb-2">
              <div className={`w-2 h-2 rounded-full ${row.color}`} />
              <span className="text-xs text-slate-600 flex-1">{row.label}</span>
              <span className="text-sm font-bold text-slate-800">{row.value}</span>
            </div>
          ))}
        </div>

        {/* Moderation */}
        <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100">
          <h3 className="text-sm font-bold text-slate-700 mb-4 flex items-center gap-2"><ShieldCheck size={15} /> Moderation</h3>
          {[
            { label: 'Flagged (action needed)', value: data.moderation.flagged_pending,  color: 'bg-red-400' },
            { label: 'Image review queue',      value: data.moderation.image_pending,    color: 'bg-amber-400' },
            { label: 'Approved today',          value: data.moderation.approved_today,   color: 'bg-emerald-500' },
          ].map(row => (
            <div key={row.label} className="flex items-center gap-3 mb-2">
              <div className={`w-2 h-2 rounded-full ${row.color}`} />
              <span className="text-xs text-slate-600 flex-1">{row.label}</span>
              <span className="text-sm font-bold text-slate-800">{row.value}</span>
            </div>
          ))}
          <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-3">
            <div className="w-2 h-2 rounded-full bg-slate-300" />
            <span className="text-xs text-slate-600 flex-1">Total bids</span>
            <span className="text-sm font-bold text-slate-800">{data.bids.total}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Sellers tab ────────────────────────────────────────────────────────────────

const SellersTab = () => {
  const navigate = useNavigate();
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState('');
  const [statusF, setStatusF] = useState('');
  const [toast,   setToast]   = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (statusF) params.status = statusF;
    if (search)  params.search = search;
    api.get('/admin/sellers/', { params })
      .then(r => setSellers(r.data))
      .catch(() => showToast('Failed to load sellers.', 'error'))
      .finally(() => setLoading(false));
  }, [statusF, search]);

  useEffect(() => { load(); }, [load]);

  const quickVerify = async (id) => {
    try {
      const r = await api.patch(`/admin/sellers/${id}/`, { status: 'verified' });
      setSellers(prev => prev.map(s => s.id === id ? { ...s, ...r.data } : s));
      showToast('Seller verified.');
    } catch (e) {
      showToast(e.response?.data?.detail || 'Update failed.', 'error');
    }
  };

  const openDetail = (s) => navigate(`/guliohub/sellers/${s.id}`, { state: { seller: s } });

  return (
    <div>
      <Toast msg={toast?.msg} type={toast?.type} />

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <SearchBar value={search} onChange={setSearch} placeholder="Business / email…" />
        <FilterSelect value={statusF} onChange={setStatusF} options={[
          { value: '', label: 'All statuses' },
          { value: 'pending',   label: 'Pending' },
          { value: 'verified',  label: 'Verified' },
          { value: 'rejected',  label: 'Rejected' },
          { value: 'suspended', label: 'Suspended' },
        ]} />
        <Btn variant="ghost" onClick={load}><RefreshCw size={13} /> Refresh</Btn>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="w-7 h-7 border-2 border-teal-400 border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-100 shadow-sm">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-slate-400 font-semibold uppercase tracking-wide" style={{ backgroundColor: `${NAV}08` }}>
                {['Business', 'Owner', 'Ownership', 'Region', 'Sell Type', 'Registered', 'Status', 'Products', 'Applied', 'Actions'].map(h => (
                  <th key={h} className="px-4 py-3 text-left whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {sellers.length === 0
                ? <tr><td colSpan={10} className="text-center py-10 text-slate-400">No sellers found.</td></tr>
                : sellers.map(s => (
                  <tr
                    key={s.id}
                    className="hover:bg-slate-50 transition-colors cursor-pointer"
                    onClick={() => openDetail(s)}
                  >
                    <td className="px-4 py-3">
                      <p className="font-semibold text-slate-800">{s.business_name}</p>
                      <p className="text-xs text-slate-400">{s.business_type}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-slate-700">{s.user_name || '—'}</p>
                      <p className="text-xs text-slate-400">{s.user_email}</p>
                    </td>
                    <td className="px-4 py-3 text-slate-500 text-xs capitalize">{s.ownership_type || '—'}</td>
                    <td className="px-4 py-3 text-slate-600">{s.region}</td>
                    <td className="px-4 py-3">
                      <Badge label={s.sell_type} color={s.sell_type === 'both' ? 'teal' : 'blue'} />
                    </td>
                    <td className="px-4 py-3">
                      {s.is_registered
                        ? <span className="text-xs font-bold text-blue-600">✔ Yes</span>
                        : <span className="text-xs text-slate-400">No</span>
                      }
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={s.status} /></td>
                    <td className="px-4 py-3 font-bold text-slate-700">{s.product_count}</td>
                    <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{fmtDate(s.created_at)}</td>
                    <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                      <div className="flex items-center gap-1.5">
                        <Btn variant="ghost" onClick={() => openDetail(s)}><Eye size={13} /> View</Btn>
                        {s.status === 'pending' && (
                          <Btn variant="success" onClick={() => quickVerify(s.id)}>
                            <CheckCircle size={13} /> Verify
                          </Btn>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ── Products tab ───────────────────────────────────────────────────────────────

const EMPTY_PRODUCT = { name: '', price: '', stock: '1', description: '', location: '', category_id: '', is_featured: false, status: 'active' };

const ProductsTab = () => {
  const [products, setProducts] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState('');
  const [statusF,  setStatusF]  = useState('');
  const [toast,    setToast]    = useState(null);
  const [detail,   setDetail]   = useState(null);
  const [creating, setCreating] = useState(false);
  const [newForm,  setNewForm]  = useState(EMPTY_PRODUCT);
  const [newFiles, setNewFiles] = useState([]);
  const [saving,   setSaving]   = useState(false);
  const [cats,     setCats]     = useState([]);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (statusF) params.status = statusF;
    if (search)  params.search = search;
    api.get('/admin/products/', { params })
      .then(r => setProducts(r.data))
      .catch(() => showToast('Failed to load products.', 'error'))
      .finally(() => setLoading(false));
  }, [statusF, search]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    axios.get(`${API}/categories/`).then(r => setCats(r.data)).catch(() => {});
  }, []);

  const openCreate = () => { setNewForm(EMPTY_PRODUCT); setNewFiles([]); setCreating(true); };

  const submitCreate = async () => {
    if (!newForm.name || !newForm.price || !newForm.description) {
      showToast('Name, price and description are required.', 'error'); return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(newForm).forEach(([k, v]) => fd.append(k, v));
      newFiles.forEach(f => fd.append('images', f));
      const r = await api.post('/admin/products/', fd);
      showToast(`Product "${r.data.name}" created as ${r.data.status}.`);
      setCreating(false);
      load();
    } catch (e) {
      showToast(e.response?.data?.detail || 'Failed to create product.', 'error');
    } finally { setSaving(false); }
  };

  const patch = async (id, payload) => {
    try {
      const r = await api.patch(`/admin/products/${id}/`, payload);
      setProducts(prev => prev.map(p => p.id === id ? { ...p, ...r.data } : p));
      showToast('Product updated.');
      if (detail?.id === id) setDetail(prev => ({ ...prev, ...r.data }));
    } catch (e) {
      showToast(e.response?.data?.detail || 'Update failed.', 'error');
    }
  };

  const deleteProduct = async (id, name) => {
    if (!window.confirm(`Permanently delete "${name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/products/${id}/`);
      setProducts(prev => prev.filter(p => p.id !== id));
      if (detail?.id === id) setDetail(null);
      showToast('Product deleted.');
    } catch (e) {
      showToast(e.response?.data?.detail || 'Delete failed.', 'error');
    }
  };

  const nf = (k, v) => setNewForm(f => ({ ...f, [k]: v }));

  return (
    <div>
      <Toast msg={toast?.msg} type={toast?.type} />

      {/* Create product modal */}
      {creating && (
        <Modal open title="New Product Listing" onClose={() => setCreating(false)} width="max-w-2xl">
          <div className="space-y-4 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold text-slate-500 block mb-1">Product Name *</label>
                <input className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-300"
                  value={newForm.name} onChange={e => nf('name', e.target.value)} placeholder="e.g. Solar Panel 200W" />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 block mb-1">Price (Tsh) *</label>
                <input type="number" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-300"
                  value={newForm.price} onChange={e => nf('price', e.target.value)} placeholder="85000" />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 block mb-1">Stock</label>
                <input type="number" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-300"
                  value={newForm.stock} onChange={e => nf('stock', e.target.value)} />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 block mb-1">Location</label>
                <input className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-300"
                  value={newForm.location} onChange={e => nf('location', e.target.value)} placeholder="Dar es Salaam" />
              </div>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 block mb-1">Category</label>
              <select className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-300"
                value={newForm.category_id} onChange={e => nf('category_id', e.target.value)}>
                <option value="">— No category —</option>
                {cats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 block mb-1">Description *</label>
              <textarea rows={3} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-300"
                value={newForm.description} onChange={e => nf('description', e.target.value)} placeholder="Describe the product in detail…" />
            </div>
            <div className="flex items-center gap-4">
              <div>
                <label className="text-xs font-bold text-slate-500 block mb-1">Status</label>
                <select className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-300"
                  value={newForm.status} onChange={e => nf('status', e.target.value)}>
                  <option value="active">Active (Live)</option>
                  <option value="pending">Pending Review</option>
                </select>
              </div>
              <div className="flex items-center gap-2 mt-4">
                <input type="checkbox" id="nf_featured" checked={newForm.is_featured}
                  onChange={e => nf('is_featured', e.target.checked)} className="w-4 h-4 accent-teal-600" />
                <label htmlFor="nf_featured" className="text-xs font-bold text-slate-600 flex items-center gap-1">
                  <Star size={12} className="text-amber-400" fill="currentColor" /> Feature this product
                </label>
              </div>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 block mb-1">Photos</label>
              <input type="file" multiple accept="image/*"
                onChange={e => setNewFiles(Array.from(e.target.files))}
                className="block w-full text-sm text-slate-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-bold file:bg-teal-50 file:text-teal-700 hover:file:bg-teal-100 cursor-pointer" />
              {newFiles.length > 0 && (
                <div className="flex gap-2 mt-2 flex-wrap">
                  {newFiles.map((f, i) => (
                    <div key={i} className="relative">
                      <img src={URL.createObjectURL(f)} alt="" className="w-16 h-16 object-cover rounded-lg border border-slate-200" />
                      {i === 0 && <span className="absolute bottom-0 left-0 right-0 text-center text-white text-[8px] font-bold bg-slate-700/70 rounded-b-lg py-0.5">Cover</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex gap-2 pt-1">
              <Btn variant="primary" onClick={submitCreate} disabled={saving}>
                <Plus size={13} /> {saving ? 'Creating…' : 'Create Product'}
              </Btn>
              <Btn variant="ghost" onClick={() => setCreating(false)}>Cancel</Btn>
            </div>
          </div>
        </Modal>
      )}

      {detail && (
        <Modal open title={detail.name} onClose={() => setDetail(null)} width="max-w-2xl">
          <div className="space-y-4 text-sm">
            {detail.image && (
              <img src={detail.image} alt={detail.name}
                   className="w-full h-40 object-cover rounded-xl" />
            )}
            <div className="grid grid-cols-2 gap-3">
              {[
                ['Seller',    detail.seller_name],
                ['Category',  detail.category || '—'],
                ['Price',     fmtTsh(detail.price)],
                ['Stock',     detail.stock],
                ['Location',  detail.location],
                ['Views',     detail.view_count],
              ].map(([k, v]) => (
                <div key={k}>
                  <p className="text-xs text-slate-400 font-medium">{k}</p>
                  <p className="font-semibold text-slate-700">{v}</p>
                </div>
              ))}
            </div>
            <p className="text-slate-600 text-xs leading-relaxed">{detail.description}</p>
            {detail.moderation_flags?.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-xs font-bold text-red-700 mb-1 flex items-center gap-1">
                  <AlertTriangle size={12} /> Moderation Flags
                </p>
                <p className="text-xs text-red-600">{detail.moderation_flags.join(', ')}</p>
              </div>
            )}
            <div>
              <p className="text-xs text-slate-400 font-medium mb-1">Admin Notes</p>
              <textarea rows={2} defaultValue={detail.admin_notes || ''} id="prod_notes_input"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-300" />
            </div>
            <div className="flex flex-wrap gap-2 pt-1">
              <Btn variant="success" onClick={() => patch(detail.id, { status: 'active', admin_notes: document.getElementById('prod_notes_input').value })}>
                <CheckCircle size={13} /> Approve
              </Btn>
              <Btn variant="ghost" onClick={() => patch(detail.id, { status: 'rejected', admin_notes: document.getElementById('prod_notes_input').value })}>
                <XCircle size={13} /> Reject
              </Btn>
              <Btn variant={detail.is_featured ? 'outline' : 'primary'}
                onClick={() => patch(detail.id, { is_featured: !detail.is_featured })}>
                {detail.is_featured ? <><StarOff size={13} /> Unfeature</> : <><Star size={13} /> Feature</>}
              </Btn>
              <Btn variant="ghost" onClick={() => patch(detail.id, { admin_notes: document.getElementById('prod_notes_input').value })}>
                Save Notes
              </Btn>
              <Btn variant="danger" onClick={() => deleteProduct(detail.id, detail.name)}>
                <Trash2 size={13} /> Delete
              </Btn>
            </div>
          </div>
        </Modal>
      )}

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <SearchBar value={search} onChange={setSearch} placeholder="Product / seller…" />
        <FilterSelect value={statusF} onChange={setStatusF} options={[
          { value: '',         label: 'All statuses' },
          { value: 'pending',  label: 'Pending' },
          { value: 'active',   label: 'Active' },
          { value: 'draft',    label: 'Draft' },
          { value: 'paused',   label: 'Paused' },
          { value: 'flagged',  label: 'Flagged' },
          { value: 'rejected', label: 'Rejected' },
          { value: 'sold',     label: 'Sold' },
        ]} />
        <Btn variant="ghost" onClick={load}><RefreshCw size={13} /> Refresh</Btn>
        <Btn variant="primary" onClick={openCreate}><Plus size={13} /> New Product</Btn>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="w-7 h-7 border-2 border-teal-400 border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-100 shadow-sm">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-slate-400 font-semibold uppercase tracking-wide" style={{ backgroundColor: `${NAV}08` }}>
                {['Product', 'Seller', 'Category', 'Price', 'Status', 'Featured', 'Views', 'Posted', 'Actions'].map(h => (
                  <th key={h} className="px-4 py-3 text-left">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {products.length === 0
                ? <tr><td colSpan={9} className="text-center py-10 text-slate-400">No products found.</td></tr>
                : products.map(p => (
                  <tr key={p.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3">
                      <p className="font-semibold text-slate-800 max-w-[160px] truncate">{p.name}</p>
                      {p.moderation_flags?.length > 0 && (
                        <p className="text-xs text-red-500 flex items-center gap-1 mt-0.5">
                          <AlertTriangle size={10} /> {p.moderation_flags.slice(0, 2).join(', ')}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-600">{p.seller_name}</td>
                    <td className="px-4 py-3 text-slate-500">{p.category || '—'}</td>
                    <td className="px-4 py-3 font-bold text-slate-700">{fmtTsh(p.price)}</td>
                    <td className="px-4 py-3"><StatusBadge status={p.status} /></td>
                    <td className="px-4 py-3">{p.is_featured ? <Star size={14} className="text-amber-400" fill="currentColor" /> : <span className="text-slate-200">—</span>}</td>
                    <td className="px-4 py-3 text-slate-500">{p.view_count}</td>
                    <td className="px-4 py-3 text-slate-500">{fmtDate(p.created_at)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <Btn variant="ghost" onClick={() => setDetail(p)}><Eye size={13} /></Btn>
                        {p.status === 'pending' || p.status === 'flagged'
                          ? <Btn variant="success" onClick={() => patch(p.id, { status: 'active' })}><CheckCircle size={13} /></Btn>
                          : null
                        }
                        {p.status === 'active' && (
                          <Btn variant={p.is_featured ? 'outline' : 'primary'}
                            onClick={() => patch(p.id, { is_featured: !p.is_featured })}>
                            {p.is_featured ? <StarOff size={13} /> : <Star size={13} />}
                          </Btn>
                        )}
                        <Btn variant="danger" onClick={() => deleteProduct(p.id, p.name)}><Trash2 size={13} /></Btn>
                      </div>
                    </td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ── Auctions tab ───────────────────────────────────────────────────────────────

const EMPTY_AUCTION = { name: '', start_bid: '', condition: 'New', description: '', location: '', category_id: '', status: 'active', ends_days: '7' };

const AuctionsTab = () => {
  const [auctions,  setAuctions]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [statusF,   setStatusF]   = useState('');
  const [toast,     setToast]     = useState(null);
  const [creating,  setCreating]  = useState(false);
  const [newForm,   setNewForm]   = useState(EMPTY_AUCTION);
  const [newFiles,  setNewFiles]  = useState([]);
  const [saving,    setSaving]    = useState(false);
  const [cats,      setCats]      = useState([]);

  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };

  const load = useCallback(() => {
    setLoading(true);
    const params = statusF ? { status: statusF } : {};
    api.get('/admin/auctions/', { params })
      .then(r => setAuctions(r.data))
      .catch(() => showToast('Failed to load auctions.', 'error'))
      .finally(() => setLoading(false));
  }, [statusF]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    axios.get(`${API}/categories/`).then(r => setCats(r.data)).catch(() => {});
  }, []);

  const nf = (k, v) => setNewForm(f => ({ ...f, [k]: v }));
  const openCreate = () => { setNewForm(EMPTY_AUCTION); setNewFiles([]); setCreating(true); };

  const submitCreate = async () => {
    if (!newForm.name || !newForm.start_bid || !newForm.description) {
      showToast('Name, starting bid and description are required.', 'error'); return;
    }
    setSaving(true);
    try {
      const ends_at = new Date(Date.now() + parseInt(newForm.ends_days, 10) * 86400000).toISOString();
      const fd = new FormData();
      fd.append('name',        newForm.name);
      fd.append('start_bid',   newForm.start_bid);
      fd.append('condition',   newForm.condition);
      fd.append('description', newForm.description);
      fd.append('location',    newForm.location);
      fd.append('status',      newForm.status);
      fd.append('ends_at',     ends_at);
      if (newForm.category_id) fd.append('category_id', newForm.category_id);
      newFiles.forEach(f => fd.append('images', f));

      console.log('[AuctionCreate] sending:', {
        name: newForm.name, start_bid: newForm.start_bid,
        status: newForm.status, ends_at, ends_days: newForm.ends_days,
        category_id: newForm.category_id || '(none)', images: newFiles.length,
      });

      const r = await api.post('/admin/auctions/', fd);
      console.log('[AuctionCreate] response:', r.data);
      showToast(`Auction "${r.data.name}" created as ${r.data.status}.`);

      if (r.data.status !== 'active') {
        console.warn('[AuctionCreate] ⚠️  status is', r.data.status, '— will NOT appear in public list until set to active');
      }

      setCreating(false);
      load();
    } catch (e) {
      console.error('[AuctionCreate] error:', e.response?.data || e.message);
      showToast(e.response?.data?.detail || 'Failed to create auction.', 'error');
    } finally { setSaving(false); }
  };

  const patch = async (id, payload) => {
    try {
      const r = await api.patch(`/admin/auctions/${id}/`, payload);
      setAuctions(prev => prev.map(a => a.id === id ? { ...a, ...r.data } : a));
      showToast('Auction updated.');
    } catch (e) {
      showToast(e.response?.data?.detail || 'Update failed.', 'error');
    }
  };

  const deleteAuction = async (id, name) => {
    if (!window.confirm(`Permanently delete auction "${name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/auctions/${id}/`);
      setAuctions(prev => prev.filter(a => a.id !== id));
      showToast('Auction deleted.');
    } catch (e) {
      showToast(e.response?.data?.detail || 'Delete failed.', 'error');
    }
  };

  return (
    <div>
      <Toast msg={toast?.msg} type={toast?.type} />

      {/* Create auction modal */}
      {creating && (
        <Modal open title="New Mnadani Listing" onClose={() => setCreating(false)} width="max-w-2xl">
          <div className="space-y-4 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="text-xs font-bold text-slate-500 block mb-1">Item Name *</label>
                <input className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-300"
                  value={newForm.name} onChange={e => nf('name', e.target.value)} placeholder="e.g. Vintage Land Rover 1987" />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 block mb-1">Starting Bid (Tsh) *</label>
                <input type="number" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-300"
                  value={newForm.start_bid} onChange={e => nf('start_bid', e.target.value)} placeholder="500000" />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 block mb-1">Condition</label>
                <select className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-300"
                  value={newForm.condition} onChange={e => nf('condition', e.target.value)}>
                  {['New', 'Used – Excellent', 'Used – Good', 'Used – Fair'].map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 block mb-1">Duration</label>
                <select className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-300"
                  value={newForm.ends_days} onChange={e => nf('ends_days', e.target.value)}>
                  {[['1', '24 hours'], ['3', '3 days'], ['7', '7 days'], ['14', '14 days'], ['30', '30 days']].map(([v, l]) => (
                    <option key={v} value={v}>{l}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 block mb-1">Location</label>
                <input className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-300"
                  value={newForm.location} onChange={e => nf('location', e.target.value)} placeholder="Dar es Salaam" />
              </div>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 block mb-1">Category</label>
              <select className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-300"
                value={newForm.category_id} onChange={e => nf('category_id', e.target.value)}>
                <option value="">— No category —</option>
                {cats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 block mb-1">Description *</label>
              <textarea rows={3} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-300"
                value={newForm.description} onChange={e => nf('description', e.target.value)} placeholder="Describe the item — condition details, history, what's included…" />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 block mb-1">Status</label>
              <select className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-300"
                value={newForm.status} onChange={e => nf('status', e.target.value)}>
                <option value="active">Active (Live)</option>
                <option value="pending">Pending Review</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 block mb-1">Photos</label>
              <input type="file" multiple accept="image/*"
                onChange={e => setNewFiles(Array.from(e.target.files))}
                className="block w-full text-sm text-slate-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-bold file:bg-teal-50 file:text-teal-700 hover:file:bg-teal-100 cursor-pointer" />
              {newFiles.length > 0 && (
                <div className="flex gap-2 mt-2 flex-wrap">
                  {newFiles.map((f, i) => (
                    <img key={i} src={URL.createObjectURL(f)} alt="" className="w-16 h-16 object-cover rounded-lg border border-slate-200" />
                  ))}
                </div>
              )}
            </div>
            <div className="flex gap-2 pt-1">
              <Btn variant="primary" onClick={submitCreate} disabled={saving}>
                <Gavel size={13} /> {saving ? 'Creating…' : 'Start Auction'}
              </Btn>
              <Btn variant="ghost" onClick={() => setCreating(false)}>Cancel</Btn>
            </div>
          </div>
        </Modal>
      )}

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <FilterSelect value={statusF} onChange={setStatusF} options={[
          { value: '',        label: 'All statuses' },
          { value: 'pending', label: 'Pending' },
          { value: 'active',  label: 'Active' },
          { value: 'ended',   label: 'Ended' },
          { value: 'flagged', label: 'Flagged' },
        ]} />
        <Btn variant="ghost" onClick={load}><RefreshCw size={13} /> Refresh</Btn>
        <Btn variant="primary" onClick={openCreate}><Plus size={13} /> New Mnadani</Btn>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="w-7 h-7 border-2 border-teal-400 border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-100 shadow-sm">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-slate-400 font-semibold uppercase tracking-wide" style={{ backgroundColor: `${NAV}08` }}>
                {['Mnadani Item', 'Seller', 'Start Bid', 'Current Bid', 'Bids', 'Ends In', 'Status', 'Created', 'Actions'].map(h => (
                  <th key={h} className="px-4 py-3 text-left">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {auctions.length === 0
                ? <tr><td colSpan={9} className="text-center py-10 text-slate-400">No auctions found.</td></tr>
                : auctions.map(a => (
                  <tr key={a.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3">
                      <p className="font-semibold text-slate-800 max-w-[160px] truncate">{a.name}</p>
                      {a.moderation_flags?.length > 0 && (
                        <p className="text-xs text-red-500 flex items-center gap-1 mt-0.5">
                          <AlertTriangle size={10} /> Flagged
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-600">{a.seller_name}</td>
                    <td className="px-4 py-3 font-semibold">{fmtTsh(a.start_bid)}</td>
                    <td className="px-4 py-3 font-bold text-emerald-600">{a.current_bid ? fmtTsh(a.current_bid) : '—'}</td>
                    <td className="px-4 py-3 font-bold text-slate-700">{a.bid_count}</td>
                    <td className="px-4 py-3 text-slate-500 flex items-center gap-1"><Clock size={11} /> {a.ends_in}</td>
                    <td className="px-4 py-3"><StatusBadge status={a.status} /></td>
                    <td className="px-4 py-3 text-slate-500">{fmtDate(a.created_at)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        {a.status === 'pending' && (
                          <Btn variant="success" onClick={() => patch(a.id, { status: 'active' })}>
                            <CheckCircle size={13} /> Approve
                          </Btn>
                        )}
                        {a.status === 'active' && (
                          <Btn variant="ghost" onClick={() => patch(a.id, { status: 'ended' })}>
                            Close
                          </Btn>
                        )}
                        {(a.status === 'pending' || a.status === 'active') && (
                          <Btn variant="danger" onClick={() => patch(a.id, { status: 'flagged' })}>
                            <AlertTriangle size={13} />
                          </Btn>
                        )}
                        <Btn variant="danger" onClick={() => deleteAuction(a.id, a.name)}><Trash2 size={13} /></Btn>
                      </div>
                    </td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ── Categories tab ─────────────────────────────────────────────────────────────

const CategoriesTab = () => {
  const [cats,    setCats]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast,   setToast]   = useState(null);
  const [modal,   setModal]   = useState(null); // null | 'add' | cat object
  const [form,    setForm]    = useState({ name: '', icon: 'tag-outline', order: 0 });
  const [saving,  setSaving]  = useState(false);

  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };

  const load = () => {
    setLoading(true);
    api.get('/admin/categories/')
      .then(r => setCats(r.data))
      .catch(() => showToast('Failed to load categories.', 'error'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openAdd  = ()    => { setForm({ name: '', icon: 'tag-outline', order: 0 }); setModal('add'); };
  const openEdit = (cat) => { setForm({ name: cat.name, icon: cat.icon, order: cat.order }); setModal(cat); };

  const submit = async () => {
    if (!form.name.trim()) { showToast('Name is required.', 'error'); return; }
    setSaving(true);
    try {
      if (modal === 'add') {
        const r = await api.post('/admin/categories/', form);
        setCats(prev => [...prev, { ...r.data, product_count: 0 }]);
        showToast('Category created.');
      } else {
        const r = await api.patch(`/admin/categories/${modal.id}/`, form);
        setCats(prev => prev.map(c => c.id === modal.id ? { ...c, ...r.data } : c));
        showToast('Category updated.');
      }
      setModal(null);
    } catch (e) {
      showToast(e.response?.data?.detail || 'Save failed.', 'error');
    } finally { setSaving(false); }
  };

  const toggleActive = async (cat) => {
    try {
      const r = await api.patch(`/admin/categories/${cat.id}/`, { is_active: !cat.is_active });
      setCats(prev => prev.map(c => c.id === cat.id ? { ...c, ...r.data } : c));
      showToast(`Category ${r.data.is_active ? 'activated' : 'deactivated'}.`);
    } catch (e) {
      showToast('Update failed.', 'error');
    }
  };

  const deleteCategory = async (cat) => {
    if (!window.confirm(`Delete "${cat.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/categories/${cat.id}/`);
      setCats(prev => prev.filter(c => c.id !== cat.id));
      showToast('Category deleted.');
    } catch (e) {
      showToast(e.response?.data?.detail || 'Delete failed.', 'error');
    }
  };

  return (
    <div>
      <Toast msg={toast?.msg} type={toast?.type} />
      <Modal open={!!modal} title={modal === 'add' ? 'Add Category' : `Edit — ${modal?.name}`} onClose={() => setModal(null)}>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-500 block mb-1">Category Name *</label>
            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-300"
              placeholder="e.g. Electronics" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 block mb-1">Icon (MaterialCommunityIcons name)</label>
            <input value={form.icon} onChange={e => setForm(f => ({ ...f, icon: e.target.value }))}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-300"
              placeholder="e.g. laptop, phone-outline, car-outline" />
            <p className="text-xs text-slate-400 mt-1">Use a valid MaterialCommunityIcons name. App reloads categories live.</p>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 block mb-1">Display Order</label>
            <input type="number" value={form.order} onChange={e => setForm(f => ({ ...f, order: Number(e.target.value) }))}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-300" />
          </div>
          <div className="flex gap-2 pt-2">
            <Btn variant="primary" onClick={submit} disabled={saving}>
              {saving ? 'Saving…' : modal === 'add' ? 'Create Category' : 'Save Changes'}
            </Btn>
            <Btn variant="ghost" onClick={() => setModal(null)}>Cancel</Btn>
          </div>
        </div>
      </Modal>

      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-slate-500">{cats.length} categories configured</p>
        <div className="flex gap-2">
          <Btn variant="ghost" onClick={load}><RefreshCw size={13} /> Refresh</Btn>
          <Btn variant="primary" onClick={openAdd}><Plus size={13} /> Add Category</Btn>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="w-7 h-7 border-2 border-teal-400 border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {cats.map(cat => (
            <div key={cat.id}
              className={`bg-white rounded-xl p-4 border shadow-sm flex items-center gap-3 ${cat.is_active ? 'border-slate-100' : 'border-slate-200 opacity-60'}`}>
              <div className="w-10 h-10 rounded-lg flex items-center justify-center font-bold text-lg"
                   style={{ backgroundColor: `${TEAL}15`, color: TEAL }}>
                {cat.name.charAt(0)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-slate-800 text-sm truncate">{cat.name}</p>
                <p className="text-xs text-slate-400">{cat.icon} · {cat.product_count} products · order {cat.order}</p>
              </div>
              <div className="flex flex-col gap-1.5 items-end">
                <Badge label={cat.is_active ? 'Active' : 'Inactive'} color={cat.is_active ? 'green' : 'gray'} />
                <div className="flex gap-1">
                  <button onClick={() => openEdit(cat)} className="text-slate-400 hover:text-teal-600 p-1 rounded"><Edit2 size={12} /></button>
                  <button onClick={() => toggleActive(cat)} className="text-slate-400 hover:text-amber-500 p-1 rounded">
                    {cat.is_active ? <XCircle size={12} /> : <CheckCircle size={12} />}
                  </button>
                  <button onClick={() => deleteCategory(cat)} className="text-slate-400 hover:text-red-500 p-1 rounded"><Trash2 size={12} /></button>
                </div>
              </div>
            </div>
          ))}
          {cats.length === 0 && <p className="col-span-3 text-center text-slate-400 py-10">No categories yet. Add the first one!</p>}
        </div>
      )}
    </div>
  );
};

// ── Moderation tab ─────────────────────────────────────────────────────────────

const ModerationTab = () => {
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [resultF, setResultF] = useState('flagged');
  const [toast,   setToast]   = useState(null);

  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };

  const load = useCallback(() => {
    setLoading(true);
    api.get('/admin/moderation/', { params: { result: resultF } })
      .then(r => setItems(r.data))
      .catch(() => showToast('Failed to load queue.', 'error'))
      .finally(() => setLoading(false));
  }, [resultF]);

  useEffect(() => { load(); }, [load]);

  const act = async (id, action) => {
    try {
      await api.patch(`/admin/moderation/${id}/`, { action });
      setItems(prev => prev.filter(i => i.id !== id));
      showToast(`Content ${action}d.`);
    } catch (e) {
      showToast(e.response?.data?.detail || 'Action failed.', 'error');
    }
  };

  return (
    <div>
      <Toast msg={toast?.msg} type={toast?.type} />
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <FilterSelect value={resultF} onChange={setResultF} options={[
          { value: 'flagged',              label: 'Flagged — needs review' },
          { value: 'pending_image_review', label: 'Image review queue' },
          { value: 'approved',             label: 'Approved' },
          { value: 'rejected',             label: 'Rejected' },
        ]} />
        <Btn variant="ghost" onClick={load}><RefreshCw size={13} /> Refresh</Btn>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="w-7 h-7 border-2 border-teal-400 border-t-transparent rounded-full animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-16">
          <ShieldCheck size={40} className="mx-auto text-emerald-300 mb-3" />
          <p className="text-slate-400 text-sm">No items in this queue. All clear!</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map(item => (
            <div key={item.id}
              className="bg-white rounded-xl border border-slate-100 shadow-sm p-4 flex flex-col md:flex-row md:items-center gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Badge label={item.content_type} color="teal" />
                  <StatusBadge status={item.result} />
                  {item.content_status && <Badge label={`listing: ${item.content_status}`} color="gray" />}
                </div>
                <p className="font-bold text-slate-800 text-sm">{item.content_name || `ID #${item.content_id}`}</p>
                {item.flags?.length > 0 && (
                  <p className="text-xs text-red-500 flex items-center gap-1 mt-1">
                    <AlertTriangle size={10} /> {item.flags.join(' · ')}
                  </p>
                )}
                <p className="text-xs text-slate-400 mt-1">
                  Checked {fmtDate(item.checked_at)}
                  {item.reviewed_by ? ` · Reviewed by ${item.reviewed_by}` : ''}
                </p>
              </div>
              {(item.result === 'flagged' || item.result === 'pending_image_review') && (
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Btn variant="success" onClick={() => act(item.id, 'approve')}>
                    <CheckCircle size={13} /> Approve
                  </Btn>
                  <Btn variant="danger" onClick={() => act(item.id, 'reject')}>
                    <XCircle size={13} /> Reject
                  </Btn>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Config tab ─────────────────────────────────────────────────────────────────

const ConfigTab = () => {
  const [config,  setConfig]  = useState(null);
  const [saving,  setSaving]  = useState(false);
  const [toast,   setToast]   = useState(null);
  const [form,    setForm]    = useState({});

  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3500); };

  useEffect(() => {
    api.get('/admin/config/')
      .then(r => { setConfig(r.data); setForm(r.data); })
      .catch(() => showToast('Failed to load config.', 'error'));
  }, []);

  const cf = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch('/admin/config/', form);
      setConfig(r.data);
      setForm(r.data);
      showToast('Configuration saved.');
    } catch (e) {
      showToast(e.response?.data?.detail || 'Save failed.', 'error');
    } finally { setSaving(false); }
  };

  if (!config) return (
    <div className="flex justify-center py-16">
      <div className="w-7 h-7 border-2 border-teal-400 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const pctInput = (key, label, hint) => (
    <div>
      <label className="text-xs font-bold text-slate-500 block mb-1">{label}</label>
      <div className="flex items-center gap-2">
        <input type="number" step="0.01" min="0" max="100"
          className="w-28 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-300"
          value={form[key] ?? ''} onChange={e => cf(key, e.target.value)} />
        <span className="text-xs text-slate-400">%{hint ? ` — ${hint}` : ''}</span>
      </div>
    </div>
  );

  const intInput = (key, label, suffix, hint) => (
    <div>
      <label className="text-xs font-bold text-slate-500 block mb-1">{label}</label>
      <div className="flex items-center gap-2">
        <input type="number" min="0"
          className="w-28 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-300"
          value={form[key] ?? ''} onChange={e => cf(key, e.target.value)} />
        <span className="text-xs text-slate-400">{suffix}{hint ? ` — ${hint}` : ''}</span>
      </div>
    </div>
  );

  return (
    <div className="space-y-8 max-w-2xl">
      <Toast msg={toast?.msg} type={toast?.type} />

      {/* Listing pricing tiers */}
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-5">
        <h3 className="text-sm font-bold text-slate-700 mb-1 flex items-center gap-2">
          <DollarSign size={15} style={{ color: NAV }} /> Listing Pricing Tiers
        </h3>
        <p className="text-xs text-slate-400 mb-4">% of item value charged to list for each duration period</p>
        <div className="grid grid-cols-2 gap-4">
          {pctInput('tier_1mo_pct',  '1 Month',  'e.g. 1%')}
          {pctInput('tier_3mo_pct',  '3 Months', 'e.g. 2%')}
          {pctInput('tier_1yr_pct',  '1 Year',   'e.g. 5%')}
          {pctInput('tier_3yr_pct',  '3 Years',  'e.g. 10%')}
          {pctInput('tier_5yr_pct',  '5 Years',  'e.g. 15%')}
          {intInput('free_listing_days', 'Free Listing Period', 'days', 'sellers get this many days free')}
        </div>
      </div>

      {/* Contact unlock */}
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-5">
        <h3 className="text-sm font-bold text-slate-700 mb-1 flex items-center gap-2">
          <Users size={15} style={{ color: NAV }} /> Contact Unlock
        </h3>
        <p className="text-xs text-slate-400 mb-4">Fee to unlock seller contact details, as a % of the item's price</p>
        {pctInput('contact_unlock_pct', 'Unlock Cost', 'of item value')}
      </div>

      {/* Auto-deletion */}
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-5">
        <h3 className="text-sm font-bold text-slate-700 mb-1 flex items-center gap-2">
          <Clock size={15} style={{ color: NAV }} /> Auto-Deletion
        </h3>
        <p className="text-xs text-slate-400 mb-4">Automatically remove expired listings after their listing period ends</p>
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <button onClick={() => cf('auto_delete_enabled', !form.auto_delete_enabled)}
              className="flex items-center gap-2 text-sm font-semibold text-slate-700">
              {form.auto_delete_enabled
                ? <ToggleRight size={26} style={{ color: TEAL }} />
                : <ToggleLeft size={26} className="text-slate-300" />}
              {form.auto_delete_enabled ? 'Enabled' : 'Disabled'}
            </button>
          </div>
          {intInput('default_listing_days', 'Default Listing Duration', 'days', 'used when no tier is specified')}
        </div>
      </div>

      <div className="flex gap-2">
        <button onClick={save} disabled={saving}
          className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-bold text-white transition-opacity disabled:opacity-60"
          style={{ backgroundColor: NAV }}>
          <Save size={14} /> {saving ? 'Saving…' : 'Save Configuration'}
        </button>
      </div>

      <p className="text-xs text-slate-400">
        Last updated: {new Date(config.updated_at).toLocaleString()}
      </p>
    </div>
  );
};

// ── Root GulioHub admin component ──────────────────────────────────────────────

const TABS = [
  { id: 'analytics',  label: 'Analytics',   icon: BarChart2,   Component: AnalyticsTab  },
  { id: 'sellers',    label: 'Sellers',      icon: Users,       Component: SellersTab    },
  { id: 'products',   label: 'MarketPlace',  icon: ShoppingBag, Component: ProductsTab   },
  { id: 'auctions',   label: 'Mnadani',      icon: Gavel,       Component: AuctionsTab   },
  { id: 'categories', label: 'Categories',   icon: Tag,         Component: CategoriesTab },
  { id: 'moderation', label: 'Moderation',   icon: ShieldCheck, Component: ModerationTab },
  { id: 'config',     label: 'Config',       icon: Settings,    Component: ConfigTab     },
];

const GulioHub = () => {
  const [activeTab, setActiveTab] = useState('analytics');
  const active = TABS.find(t => t.id === activeTab);

  return (
    <div className="p-6 space-y-6">
      {/* Page header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: NAV }}>
          <ShoppingBag size={20} color="#fff" />
        </div>
        <div>
          <h1 className="text-lg font-bold text-slate-800">GulioHUB</h1>
          <p className="text-xs text-slate-400">Manage sellers, MarketPlace products, Mnadani auctions, categories and moderation</p>
        </div>
      </div>

      {/* Tab nav */}
      <div className="flex gap-1 bg-slate-100 p-1 rounded-xl overflow-x-auto">
        {TABS.map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold whitespace-nowrap transition-all ${
                isActive ? 'bg-white shadow text-slate-800' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <Icon size={13} style={isActive ? { color: NAV } : {}} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Active tab content */}
      <div>
        {active && <active.Component />}
      </div>
    </div>
  );
};

export default GulioHub;
